import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  User,
  Mail,
  Download
} from 'lucide-react';
import api from '../utils/api';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import './EscalationReport.css';
import type {
  EscalationEmployee,
  EmployeeAssignment,
  AssignmentEmail,
  AssignmentStatusFilter,
  EscalationSortField,
} from '../types';

const EscalationReport: React.FC = () => {
  const [employees, setEmployees] = useState<EscalationEmployee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<string>('7');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<EscalationSortField>('lastUpdated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedEmployee, setSelectedEmployee] = useState<EscalationEmployee | null>(null);
  const [employeeAssignments, setEmployeeAssignments] = useState<EmployeeAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState<boolean>(false);
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState<AssignmentStatusFilter>('all');

  const fetchEscalationData = useCallback(async (): Promise<void> => {
    try {
      console.log('fetchEscalationData: Starting...', { dateRange, customStartDate, customEndDate });
      setLoading(true);

      let startDate: Date, endDate: Date;

      if (dateRange === 'custom' && customStartDate && customEndDate) {
        startDate = startOfDay(new Date(customStartDate));
        endDate = endOfDay(new Date(customEndDate));
      } else {
        const days = parseInt(dateRange);
        if (isNaN(days) || days <= 0) {
          console.error('Invalid date range:', dateRange);
          setLoading(false);
          return;
        }
        endDate = endOfDay(new Date());
        startDate = startOfDay(subDays(new Date(), days));
      }

      console.log('API Call: /api/reports/escalation', { startDate, endDate });

      const response = await api.get('/reports/escalation', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });

      console.log('API Response:', response.data);

      if (response.data.success) {
        setEmployees(response.data.data);
        console.log('Employees set:', response.data.data.length);
      }
    } catch (error) {
      console.error('Error fetching escalation data:', error);
      setEmployees([]); // Set empty array on error to prevent undefined issues
    } finally {
      setLoading(false);
      console.log('fetchEscalationData: Completed');
    }
  }, [dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    console.log('EscalationReport: useEffect triggered, fetching data...');
    fetchEscalationData();
  }, [fetchEscalationData]);

  const fetchEmployeeAssignments = async (employeeId: string): Promise<void> => {
    try {
      setLoadingAssignments(true);
      const response = await api.get(`/reports/employee/${employeeId}/assignments`);

      if (response.data.success) {
        setEmployeeAssignments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching employee assignments:', error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleEmployeeClick = (employee: EscalationEmployee): void => {
    if (selectedEmployee && selectedEmployee.id === employee.id) {
      // If clicking the same employee, close the detail view
      setSelectedEmployee(null);
      setEmployeeAssignments([]);
      setAssignmentStatusFilter('all');
    } else {
      // Open detail view for new employee
      setSelectedEmployee(employee);
      setAssignmentStatusFilter('all');
      fetchEmployeeAssignments(employee.id);
    }
  };

  const getFilteredAssignments = (): EmployeeAssignment[] => {
    let filteredAssignments: EmployeeAssignment[];

    if (assignmentStatusFilter === 'all') {
      filteredAssignments = employeeAssignments;
    } else {
      filteredAssignments = employeeAssignments.filter(assignment => {
        switch (assignmentStatusFilter) {
          case 'assigned':
            return assignment.status === 'assigned';
          case 'responded':
            return assignment.status === 'responded';
          case 'closed':
            return assignment.status === 'closed';
          default:
            return true;
        }
      });
    }

    // Sort threads by date in ascending order (oldest first)
    return filteredAssignments.sort((a, b) => {
      const dateA = new Date(a.lastEmailDate || a.assignmentDate || 0).getTime();
      const dateB = new Date(b.lastEmailDate || b.assignmentDate || 0).getTime();
      return dateA - dateB; // Ascending order (oldest first)
    });
  };

  const handleMarkAsClosed = async (threadId: string, employeeId: string): Promise<void> => {
    try {
      const response = await api.post(`/reports/employee/${employeeId}/thread/${threadId}/close`);

      if (response.data.success) {
        // Refresh the assignments to show updated status
        await fetchEmployeeAssignments(employeeId);
        // Refresh the escalation data to show updated closed count
        await fetchEscalationData();

        console.log('Thread marked as closed successfully');

        // Show success message to user
        alert(`Thread closed successfully! ${response.data.employeesUpdated || 1} employee(s) updated.`);
      }
    } catch (error) {
      console.error('Failed to mark thread as closed:', error);
      alert('Failed to mark thread as closed. Please try again.');
    }
  };

  const handleSort = (field: EscalationSortField): void => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortEmployees = (employeeList: EscalationEmployee[]): EscalationEmployee[] => {
    return [...employeeList].sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (sortBy) {
        case 'lastUpdated':
          aValue = new Date(a.lastEmailDate || a.assignmentDate || 0);
          bValue = new Date(b.lastEmailDate || b.assignmentDate || 0);
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'emailsReceived':
          aValue = a.emailsReceived;
          bValue = b.emailsReceived;
          break;
        case 'repliedWithin24h':
          aValue = a.repliedWithin24h;
          bValue = b.repliedWithin24h;
          break;
        case 'pendingWithClient':
          aValue = a.pendingWithClient;
          bValue = b.pendingWithClient;
          break;
        case 'pendingWithEmployee':
          aValue = a.pendingWithEmployee;
          bValue = b.pendingWithEmployee;
          break;
        case 'over24h':
          aValue = a.over24h;
          bValue = b.over24h;
          break;
        case 'over1Day':
          aValue = a.over1Day;
          bValue = b.over1Day;
          break;
        case 'closed':
          aValue = a.closed || 0;
          bValue = b.closed || 0;
          break;
        case 'sentiment':
          aValue = a.sentiment?.averageScore || 0;
          bValue = b.sentiment?.averageScore || 0;
          break;
        default:
          aValue = new Date(a.lastEmailDate || a.assignmentDate || 0);
          bValue = new Date(b.lastEmailDate || b.assignmentDate || 0);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (aValue instanceof Date && bValue instanceof Date) {
        return sortOrder === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
      } else {
        return sortOrder === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
      }
    });
  };



  const exportReport = (): void => {
    const csvContent = generateCSV();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `escalation-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const generateCSV = (): string => {
    const headers = [
      'Employee Name',
      'Email',
      'Emails Received',
      'Pending with Client',
      'Pending with Employee',
      'Over 24h',
      'Over 1 Day',
      'Closed',
      'Client Sentiment',
      'Sentiment Score',
      'Response Rate (%)'
    ];

    const rows = employees.map(emp => [
      emp.name,
      emp.email,
      emp.emailsReceived,
      emp.repliedWithin24h,
      emp.pendingWithClient,
      emp.pendingWithEmployee,
      emp.over24h,
      emp.over1Day,
      emp.closed || 0,
      emp.sentiment ? `${emp.sentiment.positive > emp.sentiment.negative ? 'Positive' : emp.sentiment.negative > emp.sentiment.positive ? 'Negative' : 'Neutral'}` : 'N/A',
      emp.sentiment?.averageScore || 'N/A',
      emp.emailsReceived > 0 ? ((emp.repliedWithin24h / emp.emailsReceived) * 100).toFixed(1) : '0'
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const getResponseRate = (employee: EscalationEmployee): string => {
    if (employee.emailsReceived === 0) return '0';
    return ((employee.repliedWithin24h / employee.emailsReceived) * 100).toFixed(1);
  };

  const getStatusColor = (rate: number | string): string => {
    const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (numRate >= 90) return 'success';
    if (numRate >= 70) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <div className="escalation-report">
        <div className="loading">Loading escalation report...</div>
      </div>
    );
  }

  // Safety check to prevent rendering issues
  if (!employees || !Array.isArray(employees)) {
    console.warn('Employees data is invalid:', employees);
    return (
      <div className="escalation-report">
        <div className="error">Error: Invalid employee data</div>
      </div>
    );
  }

  const sortedEmployees = sortEmployees(employees);

  return (
    <div className="escalation-report">
      <div className="report-header">
        <div className="header-content">
          <h1>Escalation Report</h1>
          <p className="report-subtitle">
            Employee performance metrics and escalation tracking
          </p>
        </div>

        <div className="header-actions">
          <button
            className="export-btn"
            onClick={exportReport}
            title="Export to CSV"
          >
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Date Range:</label>
            <select
              value={dateRange}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDateRange(e.target.value)}
              className="date-range-select"
            >
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <div className="custom-date-inputs">
              <div className="date-input-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomStartDate(e.target.value)}
                  className="date-input"
                />
              </div>
              <div className="date-input-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomEndDate(e.target.value)}
                  className="date-input"
                />
              </div>
            </div>
          )}

          <div className="filter-group">
            <label>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSortBy(e.target.value as EscalationSortField)}
              className="sort-select"
            >
              <option value="lastUpdated">Last Updated</option>
              <option value="name">Employee Name</option>
              <option value="emailsReceived">Emails Received</option>
              <option value="pendingWithClient">Pending with Client</option>
              <option value="pendingWithEmployee">Pending with Employee</option>
              <option value="over24h">Over 24h</option>
              <option value="over1Day">Over 1 Day</option>
            </select>

          </div>

          <button
            className="submit-filters-btn"
            onClick={fetchEscalationData}
          >
            Fetch
          </button>
        </div>
      </div>

      <div className="report-summary">
        <div className="summary-card">
          <div className="summary-icon">
            <User size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-value">{employees.length}</div>
            <div className="summary-label">Total Employees</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon">
            <Mail size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-value">
              {employees.reduce((sum, emp) => sum + emp.emailsReceived, 0)}
            </div>
            <div className="summary-label">Total Emails</div>
          </div>
        </div>



        <div className="summary-card">
          <div className="summary-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="summary-content">
            <div className="summary-value">
              {employees.reduce((sum, emp) => sum + emp.over24h, 0)}
            </div>
            <div className="summary-label">Over 24h</div>
          </div>
        </div>
      </div>

      <div className="report-table-container">
        <table className="escalation-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable">
                Employee
                {sortBy === 'name' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th onClick={() => handleSort('emailsReceived')} className="sortable">
                Emails Received
                {sortBy === 'emailsReceived' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th onClick={() => handleSort('pendingWithClient')} className="sortable">
                Pending with Client
                {sortBy === 'pendingWithClient' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th onClick={() => handleSort('pendingWithEmployee')} className="sortable">
                Pending with Employee
                {sortBy === 'pendingWithEmployee' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th onClick={() => handleSort('over24h')} className="sortable">
                Over 24h
                {sortBy === 'over24h' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th onClick={() => handleSort('over1Day')} className="sortable">
                Over 1 Day
                {sortBy === 'over1Day' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th onClick={() => handleSort('closed')} className="sortable">
                Closed
                {sortBy === 'closed' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th onClick={() => handleSort('sentiment')} className="sortable">
                Client Sentiment
                {sortBy === 'sentiment' && <span className="sort-indicator">{sortOrder === 'asc' ? '\u2191' : '\u2193'}</span>}
              </th>
              <th>Response Rate</th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees.map((employee) => {
              const responseRate = getResponseRate(employee);
              const statusColor = getStatusColor(responseRate);

              return (
                <tr
                  key={employee.id}
                  className={`employee-row ${selectedEmployee?.id === employee.id ? 'selected' : ''}`}
                  onClick={() => handleEmployeeClick(employee)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="employee-info">
                    <div className="employee-name">{employee.name}</div>
                    <div className="employee-email">{employee.email}</div>
                  </td>

                  <td className="metric-cell">
                    <div className="metric-value">{employee.emailsReceived}</div>
                  </td>



                  <td className="metric-cell">
                    <div className="metric-value warning">{employee.pendingWithClient}</div>
                    {employee.pendingWithClient > 0 && (
                      <div className="metric-detail">
                        Oldest: {employee.oldestPendingClient ? format(new Date(employee.oldestPendingClient), 'MMM dd') : 'N/A'}
                      </div>
                    )}
                  </td>

                  <td className="metric-cell">
                    <div className="metric-value warning">{employee.pendingWithEmployee}</div>
                    {employee.pendingWithEmployee > 0 && (
                      <div className="metric-detail">
                        Oldest: {employee.oldestPendingEmployee ? format(new Date(employee.oldestPendingEmployee), 'MMM dd') : 'N/A'}
                      </div>
                    )}
                  </td>

                  <td className="metric-cell">
                    <div className="metric-value danger">{employee.over24h}</div>
                  </td>

                  <td className="metric-cell">
                    <div className="metric-value danger">{employee.over1Day}</div>
                  </td>

                  <td className="metric-cell">
                    <div className="metric-value success">{employee.closed || 0}</div>
                  </td>

                  <td className="sentiment-cell">
                    {employee.sentiment ? (
                      <div className="sentiment-display">
                        <div className="sentiment-summary">
                          <span className={`sentiment-badge ${employee.sentiment.positive > employee.sentiment.negative ? 'positive' : employee.sentiment.negative > employee.sentiment.positive ? 'negative' : 'neutral'}`}>
                            {employee.sentiment.positive > employee.sentiment.negative ? '\u{1F60A}' : employee.sentiment.negative > employee.sentiment.positive ? '\u{1F620}' : '\u{1F610}'}
                          </span>
                          <span className="sentiment-score">
                            {employee.sentiment.averageScore}
                          </span>
                        </div>
                        <div className="sentiment-breakdown">
                          <span className="sentiment-count positive">+{employee.sentiment.positive}</span>
                          <span className="sentiment-count negative">-{employee.sentiment.negative}</span>
                          <span className="sentiment-count neutral">~{employee.sentiment.neutral}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="no-sentiment">No data</div>
                    )}
                  </td>

                  <td className="response-rate-cell">
                    <div className={`response-rate ${statusColor}`}>
                      {responseRate}%
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Employee Detail View */}
      {selectedEmployee && (
        <div className="employee-detail-view">
          <div className="detail-header">
            <h3>{selectedEmployee.name} - Detailed Assignments</h3>
            <button
              className="close-detail-btn"
              onClick={() => {
                setSelectedEmployee(null);
                setEmployeeAssignments([]);
                setAssignmentStatusFilter('all');
              }}
            >
              &#10005;
            </button>
          </div>

          {loadingAssignments ? (
            <div className="loading-assignments">Loading assignments...</div>
          ) : (
            <div className="assignments-container">
              {/* Assignment Status Filter */}
              <div className="assignment-filters">
                <div className="filter-group">
                  <label>Filter by Status:</label>
                  <select
                    value={assignmentStatusFilter}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAssignmentStatusFilter(e.target.value as AssignmentStatusFilter)}
                    className="assignment-status-select"
                  >
                    <option value="all">All Statuses</option>
                    <option value="assigned">Assigned</option>
                    <option value="responded">Responded</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="filter-info">
                  <span className="filter-count">
                    Showing {getFilteredAssignments().length} of {employeeAssignments.length} assignments
                  </span>
                </div>
              </div>

              {getFilteredAssignments().length === 0 ? (
                <div className="no-assignments">
                  <p>No {assignmentStatusFilter === 'all' ? '' : assignmentStatusFilter} assignments found for this employee</p>
                </div>
              ) : (
                <div className="threads-list">
                  {getFilteredAssignments().map((thread) => (
                    <div key={thread.threadId} className="thread-item">
                      <div className="thread-header">
                        <div className="thread-status">
                          <span className={`status-badge ${thread.status === 'responded' ? 'success' : thread.status === 'closed' ? 'info' : 'warning'}`}>
                            {thread.status === 'responded' ? 'Responded' : thread.status === 'closed' ? 'Closed' : 'Pending'}
                          </span>
                          {thread.clientSentiment && (
                            <span className={`sentiment-indicator ${thread.clientSentiment || 'neutral'}`}>
                              {thread.clientSentiment === 'positive' ? '\u{1F60A}' :
                               thread.clientSentiment === 'negative' ? '\u{1F620}' : '\u{1F610}'}
                            </span>
                          )}
                        </div>
                        <div className="thread-info">
                          <div className="thread-subject">{thread.subject}</div>
                          <div className="thread-client">From: {thread.clientEmail}</div>
                          <div className="thread-date">
                            Last Email: {format(new Date(thread.lastEmailDate), 'MMM dd, yyyy HH:mm')}
                          </div>
                        </div>
                        <div className="thread-actions">
                          {thread.status !== 'closed' && (
                            <button
                              className="close-thread-btn"
                              onClick={() => handleMarkAsClosed(thread.threadId, selectedEmployee.id)}
                              title="Mark thread as closed"
                            >
                              Close
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="thread-emails">
                        <div className="emails-header">
                          <span className="emails-title">Emails in Thread ({thread.emails.length})</span>
                        </div>
                        {thread.emails.map((email) => (
                          <div key={email.id} className="email-item">
                            <div className="email-meta">
                              <span className="email-date">{format(new Date(email.emailDate), 'MMM dd, HH:mm')}</span>
                              <span className={`email-status ${email.status}`}>{email.status}</span>
                            </div>
                            <div className="email-body-preview">
                              {email.body ? email.body.substring(0, 100) + (email.body.length > 100 ? '...' : '') : 'No body content'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {employees.length === 0 && (
        <div className="no-data">
          <Mail size={48} />
          <p>No escalation data found for the selected date range</p>
        </div>
      )}
    </div>
  );
};

export default EscalationReport;
